import { useEffect, useRef, useState } from 'react';
import SockJS from 'sockjs-client';
import { Stomp } from '@stomp/stompjs';
import { useAuth } from '../auth/AuthContext';
import { getUserId } from '../utils/auth';
import { BASE_URL } from '../config';
import { useAxios } from '../hooks/useAxios';

export interface MessageResp {
  id: number;
  senderUser: string;
  receiverUser: string;
  content: string;
  sendDateTime: string;
  readDateTime?: string;
  read: boolean;
}

export const useChat = (targetUserId: string) => {
  const axiosInstance = useAxios();
  const [typingStatus, setTypingStatus] = useState(false);
  const { token, refresh } = useAuth();
  const [messages, setMessages] = useState<MessageResp[]>([]);
  const [connected, setConnected] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const clientRef = useRef<any>(null);
  const secondaryClientRef = useRef<any>(null);
  const userId = getUserId() || '';
  const PAGE_SIZE = 20;
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isAccessTokenValid = (accessToken: string | null): boolean => {
    if (!accessToken) return false;
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      return payload.exp > now;
    } catch {
      return false;
    }
  };

  const waitForClientConnection = async (ref: any, connectFn: () => Promise<void>) => {
    if (ref.current && ref.current.connected) return;

    try {
      await connectFn();
    } catch (err) {
      console.error('WebSocket connection failed:', err);
      throw err;
    }

    const maxWait = 3000;
    const interval = 100;
    let waited = 0;
    while (ref.current && !ref.current.connected && waited < maxWait) {
      await new Promise((res) => setTimeout(res, interval));
      waited += interval;
    }

    if (!ref.current || !ref.current.connected) {
      throw new Error('WebSocket connection timeout');
    }
  };

  const connectClient = async (): Promise<void> => {
    if (!targetUserId || !userId) {
      console.error('Eksik bilgi');
      return;
    }

    let tokenToUse = isAccessTokenValid(token) ? token : null;
    if (!tokenToUse && refresh) {
      const refreshed = await refresh();
      if (refreshed) {
        tokenToUse = localStorage.getItem('accessToken');
      }
    }

    if (!tokenToUse) {
      console.error('No valid tokend. WebSocket can not connect.');
      return;
    }

    const socket = new SockJS(`${BASE_URL}/ws?access_token=${tokenToUse}`);
    const client = Stomp.over(socket);
    client.debug = () => { };
    client.reconnectDelay = 5000;

    client.heartbeatIncoming = 10000;
    client.heartbeatOutgoing = 10000;

    clientRef.current = client;

    return new Promise((resolve, reject) => {
      client.connect(
        { Authorization: `Bearer ${tokenToUse}` },
        () => {
          setConnected(true);
          client.subscribe(`/user/queue/messages`, (msg) => {
            const message: MessageResp = JSON.parse(msg.body);
            if (
              !(
                (message.senderUser === userId && message.receiverUser === targetUserId) ||
                (message.senderUser === targetUserId && message.receiverUser === userId)
              )
            ) {
              return;
            }
            setMessages((prev) => {
              const alreadyExists = prev.some(
                (m) =>
                  m.senderUser === message.senderUser &&
                  m.receiverUser === message.receiverUser &&
                  m.content === message.content &&
                  Math.abs(new Date(m.sendDateTime).getTime() - new Date(message.sendDateTime).getTime()) < 100
              );
              if (alreadyExists) return prev;
              return [...prev, message];
            });
          });
          fetchMessages(0);
          resolve();
        },
        (error: any) => {
          console.error('WebSocket connection error:', error);
          setConnected(false);

          if (refresh) {
            refresh().then((refreshed) => {
              if (refreshed) {
                console.log('Token refreshed. WebSocket reconnecting...');
                setTimeout(() => connectClient(), 1100);
              } else {
                reject(error);
              }
            });
          } else {
            reject(error);
          }
        }
      );
    });
  };

  const connectSecondaryClient = async (): Promise<void> => {
    let tokenToUse = isAccessTokenValid(token) ? token : null;
    if (!tokenToUse && refresh) {
      const refreshed = await refresh();
      if (refreshed) {
        tokenToUse = localStorage.getItem('accessToken');
      }
    }

    if (!tokenToUse) {
      console.error('No valid token. Secondary WebSocket cannot connect.');
      return;
    }

    const socket = new SockJS(`${BASE_URL}/ws?access_token=${tokenToUse}`);
    const client = Stomp.over(socket);
    client.debug = () => { };
    client.reconnectDelay = 5000;

    // ✅ WebSocket canlı tutmak için
    client.heartbeatIncoming = 10000;
    client.heartbeatOutgoing = 10000;

    secondaryClientRef.current = client;

    return new Promise((resolve, reject) => {
      client.connect(
        { Authorization: `Bearer ${tokenToUse}` },
        () => {
          client.subscribe(`/user/queue/typing`, (msg) => {
            const data = JSON.parse(msg.body);
            if (data.senderId === targetUserId && data.typing) {
              setTypingStatus(true);

              // Önceki timeout varsa temizle
              if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
              }

              // Yeni timeout başlat
              typingTimeoutRef.current = setTimeout(() => {
                setTypingStatus(false);
              }, 3000);
            }
          });
          resolve();
        },
        async (error: any) => {
          console.error('Secondary WS connection error:', error);
          if (refresh) {
            const refreshed = await refresh();
            if (refreshed) {
              setTimeout(() => connectSecondaryClient(), 1000);
            } else {
              reject(error);
            }
          } else {
            reject(error);
          }
        }
      );
    });
  };

  useEffect(() => {
    if (!token || !targetUserId || !userId) return;
    connectClient().catch(() => { });
    connectSecondaryClient().catch(() => { });
    return () => {
      if (clientRef.current && clientRef.current.connected) {
        clientRef.current.disconnect(() => setConnected(false));
      }
      if (secondaryClientRef.current && secondaryClientRef.current.connected) {
        secondaryClientRef.current.disconnect(() => { });
      }
    };
  }, [token, targetUserId]);

  const sendTypingStatus = async () => {
    if (!userId || !targetUserId) return;

    try {
      await waitForClientConnection(secondaryClientRef, connectSecondaryClient);

      const payload = {
        senderId: userId,
        receiverId: targetUserId,
        typing: true,
      };

      secondaryClientRef.current.send('/app/chat.typing', {}, JSON.stringify(payload));
    } catch (err) {
      console.error('sendTypingStatus failed:', err);
    }
  };

  const fetchMessages = async (pageToFetch: number = 0) => {
    if (loading || !token) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/api/user/conversation/${targetUserId}?page=${pageToFetch}&size=${PAGE_SIZE}`
      );
      const newMessages: MessageResp[] = res.data.data;

      if (newMessages.length < PAGE_SIZE) setHasMore(false);
      if (pageToFetch === 0) {
        setMessages(newMessages);
      } else {
        setMessages((prev) => [...newMessages, ...prev]);
      }
      setPage(pageToFetch);
    } catch (err) {
      console.error('Messages could not fetch:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadOlderMessages = () => {
    if (hasMore && !loading) {
      fetchMessages(page + 1);
    }
  };

  const sendMessage = async (content: string) => {
    if (!userId) return;

    try {
      await waitForClientConnection(clientRef, connectClient);

      const messagePayload = {
        receiverId: targetUserId,
        senderId: userId,
        content,
      };

      clientRef.current.send('/app/chat.send', {}, JSON.stringify(messagePayload));
    } catch (err) {
      console.error('sendMessage failed:', err);
    }
  };

  const markAsRead = async () => {
    try {
      await axiosInstance.post(`/api/user/messages/set/read/${targetUserId}`);
    } catch (err) {
      console.error('could not send mar as read:', err);
    }
  };

  return {
    messages,
    sendMessage,
    connected,
    markAsRead,
    loadOlderMessages,
    hasMore,
    loading,
    page,
    sendTypingStatus,
    typingStatus
  };
};